import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from 'apollo-react/components/Autocomplete';

/**
 * OrderedMultiSelect - Autocomplete multi-select that preserves selection order
 *
 * The order of selected items is preserved in the value array.
 * Items are displayed as chips in the order they were selected.
 *
 * @param {string} label - Label for the autocomplete
 * @param {string} helperText - Helper text below the autocomplete
 * @param {Array} options - Available options [{ id, name, label }]
 * @param {Array} value - Selected values (array of ids in order)
 * @param {Function} onChange - Callback with ordered array of ids
 * @param {boolean} disabled - Disable the component
 * @param {boolean} required - Mark as required
 * @param {boolean} error - Show error state
 * @param {string} placeholder - Placeholder text
 * @param {Function} getOptionLabel - Custom function to get option label
 */
const OrderedMultiSelect = ({
  label,
  helperText,
  options = [],
  value = [],
  onChange,
  disabled = false,
  required = false,
  error = false,
  placeholder,
  getOptionLabel,
  ...props
}) => {
  const { t } = useTranslation();

  // Convert options to Autocomplete format
  const autocompleteSource = options.map((opt) => ({
    label: getOptionLabel ? getOptionLabel(opt) : opt.name || opt.label,
    value: opt.id,
    ...opt,
  }));

  // Get selected items in order
  const selectedItems = value
    .map((id) => options.find((opt) => opt.id === id))
    .filter(Boolean);

  // Current autocomplete value (array of selected objects in order)
  const autocompleteValue = selectedItems.map((item) => ({
    label: getOptionLabel ? getOptionLabel(item) : item.name || item.label,
    value: item.id,
    ...item,
  }));

  // Handle autocomplete change - preserves order
  const handleAutocompleteChange = useCallback(
    (event, newValue) => {
      const newIds = newValue.map((item) => item.value || item.id);
      onChange(newIds);
    },
    [onChange]
  );

  return (
    <Autocomplete
      label={label}
      placeholder={placeholder || t('Search and select...')}
      helperText={helperText}
      source={autocompleteSource}
      value={autocompleteValue}
      onChange={handleAutocompleteChange}
      multiple
      fullWidth
      disabled={disabled}
      error={error}
      required={required}
      filterSelectedOptions
      {...props}
    />
  );
};

export default OrderedMultiSelect;
